import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import { useNavigate, useParams } from "react-router-dom";
import SimpleReactValidator from "simple-react-validator";
import { errorMessage, successMessage } from "../../../utils";
import {
  createStampRewardApi,
  updateStampRewardApi,
  getStampCardRewardDetailsApi,
} from "../api";
import { AppContext } from "../../../store";
import { getCategorywiseProductsInStore } from "../../Stores/api/StoreApi";

export const useAddorEditStampReward = () => {
  const { id, rid } = useParams();
  const navigate = useNavigate();
  const { appState } = useContext(AppContext);
  const { globalData } = appState;

  const [forceUpdate, setForceUpdate] = useState(0);

  const [state, setState] = useImmer({
    isLoading: false,
    isSaving: false,
    productCategories: [],
    data: {
      stamps_unlock: null,
      reward_title: "",
      validity: "",
      description: "",
      reward_groups: [
        {
          group_title: "",
          products: [],
        },
      ],
      mobile_image: null,
      desktop_image: null,
    },
    imagePreview: {
      mobile_image: null,
      desktop_image: null,
    },
  });

  const validator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: setForceUpdate },
      validators: {
        pro: {
          message: "At least one product is required",
          rule: (productLength) => {
            return Number(productLength) >= 1;
          },
        },
      },
    }),
  );

  useEffect(() => {
    if (rid) {
      fetchStampRewardDetails(id, rid);
    }
  }, [rid]);

  const getCategorywiseProducts = useCallback(async () => {
    try {
      const res = await getCategorywiseProductsInStore();
      if (res?.success) {
        const mapped = (res.data || [])
          .map((category) => ({
            id: category.id,
            name: category.name,
            products: (category.products || [])
              .filter(
                (product) =>
                  product.status !== 0 && product.type !== "modifier",
              )
              .map((product) => ({
                value: product.id,
                label: product.name,
                type: product.type,
              })),
          }))
          .filter((category) => category.products.length > 0);

        setState((draft) => {
          draft.productCategories = mapped;
        });
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message || "Failed to load category-wise products",
      );
    }
  }, [setState]);

  useEffect(() => {
    getCategorywiseProducts();
  }, [getCategorywiseProducts]);

  const fetchStampRewardDetails = async (id, rewardId) => {
    try {
      setState((draft) => {
        draft.isLoading = true;
      });

      const res = await getStampCardRewardDetailsApi(id, rewardId);

      if (res?.success) {
        setState((draft) => {
          draft.data = res.data;
          draft.imagePreview.mobile_image = res.data.mobile_image;
          draft.data.mobile_image = null;
          draft.imagePreview.desktop_image = res.data.desktop_image;
          draft.data.desktop_image = null;
          draft.isLoading = false;
        });
      }
    } catch (err) {
      errorMessage("Failed to load reward details");
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState((draft) => {
      draft.data[name] = value;
    });
  };

  const handleDescriptionChange = (value) => {
    setState((draft) => {
      draft.data.description = value;
    });
  };

  const onImageChange = (files, name) => {
    if (!files || !files.length) return;

    const file = files[0];
    const previewUrl = URL.createObjectURL(file);

    setState((draft) => {
      // store actual file
      draft.data[name] = file;

      // store preview url
      draft.imagePreview[name] = previewUrl;
    });
  };

  const addRewardGroup = () => {
    setState((draft) => {
      draft.data.reward_groups.push({
        group_title: "",
        products: [],
      });
    });
  };

  const removeRewardGroup = (groupIndex) => {
    setState((draft) => {
      draft.data.reward_groups.splice(groupIndex, 1);
    });
  };

  const updateGroupTitle = (groupIndex, event) => {
    setState((draft) => {
      draft.data.reward_groups[groupIndex].group_title = event.target.value;
    });
  };

  const setProductsForGroup = (groupIndex, products = []) => {
    setState((draft) => {
      draft.data.reward_groups[groupIndex].products = products.map(
        (product) => ({
          id: product.value,
          name: product.label || null,
        }),
      );
    });
  };

  const removeProductFromGroup = (groupIndex, productIndex) => {
    setState((draft) => {
      draft.data.reward_groups[groupIndex].products.splice(productIndex, 1);
    });
  };

  const handleSubmit = async () => {
    if (!validator.current.allValid()) {
      validator.current.showMessages();
      setForceUpdate((f) => f + 1);
      return;
    }

    try {
      setState((draft) => {
        draft.isSaving = true;
      });
      const formData = new FormData();

      formData.append("stamps_unlock", Number(state.data.stamps_unlock));
      formData.append("reward_title", state.data.reward_title);
      formData.append("validity", Number(state.data.validity));
      formData.append("description", state.data.description);

      state.data.reward_groups.forEach((group, gIndex) => {
        // ✅ group title (single value)
        formData.append(
          `reward_groups[${gIndex}][group_title]`,
          group.group_title,
        );

        // ✅ product ids (array)
        group.products.forEach((product) => {
          formData.append(
            `reward_groups[${gIndex}][reward_product_ids][]`,
            product.id,
          );
        });
      });

      // images
      if (state.data.mobile_image) {
        formData.append("mobile_image", state.data.mobile_image);
      }
      if (state.data.desktop_image) {
        formData.append("desktop_image", state.data.desktop_image);
      }

      const res = rid
        ? await updateStampRewardApi(id, rid, formData)
        : await createStampRewardApi(id, formData);

      if (res?.success) {
        successMessage(res.message);
        navigate(`/stamp-cards/details/${id}`);
      }
    } catch (err) {
      errorMessage("Something went wrong");
    } finally {
      setState((draft) => {
        draft.isSaving = false;
      });
    }
  };

  const breadcrumbLinks = [
    { name: "Dashboard", href: "/" },
    { name: "Stamp Cards", href: "/stamp-cards" },
    { name: "Stamp Reward Mapping", href: `/stamp-cards/details/${id}` },
    { name: id ? "Edit" : "Create" },
  ];


  return {
    state,
    globalData,
    validator,
    handleInputChange,
    onImageChange,
    handleDescriptionChange,
    handleSubmit,
    productCategories: state.productCategories,
    getCategorywiseProducts,
    breadcrumbLinks,
    isEditMode: !!id,

    // group handlers
    addRewardGroup,
    removeRewardGroup,
    updateGroupTitle,

    // product handlers
    setProductsForGroup,
    removeProductFromGroup,
  };
};
